import { DashboardAsk, SkuDTO } from '@/types/api/api-v2';
import CustomPagination from '../searchCustom/CustomPagination';
import CustomSortBy from '../searchCustom/CustomSortBy';
import FilterSkus from '../FilterSkus/FilterSkus';
import SellerBidsDisplay from './SellerBidsDisplay';
import { AllSortsCustomTranslated } from '@/types/other';
import { FilterConfig } from '@/types/props/components/skus';
import { useState, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { deleteCookie, setCookie } from 'cookies-next';
import cookiesConstants from '@/constants/cookies.constants';
import SkusMobileSearchFilters from '../SkusMobile/SkusMobileSearchCustom/SkusMobileSearchFilters';
import SkusMobileSearchShowFilters from '../SkusMobile/SkusMobileSearchCustom/SkusMobileSearchShowFilters/SkusMobileSearchShowFilters';

interface SellerDesktopProps {
  allSortsCustomTranslated: AllSortsCustomTranslated[];
  asks: DashboardAsk[];
  handleSellerCardClick: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    sku: SkuDTO,
    groupedAsks?: any
  ) => void;
  name: string;
  filterConfig?: FilterConfig;
  maxBottles?: number;
  accentColor?: string;
  secondaryAccentColor?: string;
}

export default function SellerDesktop({
  allSortsCustomTranslated,
  asks,
  handleSellerCardClick,
  name,
  filterConfig,
  maxBottles,
  accentColor,
  secondaryAccentColor,
}: SellerDesktopProps) {
  const [filtersAreOpen, setFiltersAreOpen] = useState<boolean>(false);

  // Filter configuration for seller page - disable status filter
  const mobileFilterConfig: FilterConfig = {
    status: false, // Hide status filter on seller page
    category: true,
    brand: true,
    price: true,
    country: true,
    region: true,
    size: true,
  };

  useEffect(() => {
    if (filtersAreOpen) {
      setCookie(cookiesConstants.COOKIES.FILTERS_ARE_OPEN.name, 'true');
    } else {
      deleteCookie(cookiesConstants.COOKIES.FILTERS_ARE_OPEN.name);
    }

    return () => {
      deleteCookie(cookiesConstants.COOKIES.FILTERS_ARE_OPEN.name);
    };
  }, [filtersAreOpen]);

  return (
    <>
      {/* Mobile Filter Overlay */}
      <Transition
        as={Fragment}
        show={filtersAreOpen}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        unmount={false}
      >
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-bg-black tw-bg-opacity-50 xl:tw-hidden">
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-4">
            <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-white tw-shadow-xl">
              <SkusMobileSearchShowFilters
                setFiltersAreOpen={setFiltersAreOpen}
                filtersAreOpen={filtersAreOpen}
                filterConfig={mobileFilterConfig}
              />
            </div>
          </div>
        </div>
      </Transition>

      <div className="tw-container tw-mx-auto tw-px-3">
        {/* Main Content Layout */}
        <div className="tw-flex tw-flex-col tw-gap-8 xl:tw-flex-row">
          {/* Sidebar - Filters */}
          <aside className="tw-hidden xl:tw-block xl:tw-w-80 xl:tw-flex-shrink-0">
            <div className="tw-sticky tw-top-8 tw-max-h-[calc(100vh-4rem)] tw-overflow-x-auto">
              <FilterSkus
                disableRedirect={true}
                filterConfig={filterConfig ?? {}}
                accentColor={accentColor}
                secondaryAccentColor={secondaryAccentColor}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className="tw-min-w-0 tw-flex-1">
            {/* Header Section */}
            <header className="tw-mb-8 tw-w-full tw-overflow-hidden tw-rounded-2xl tw-border tw-border-gray-300/60 tw-bg-white/80 tw-p-5 tw-shadow-md tw-backdrop-blur-sm">
              <div className="tw-flex tw-items-center tw-justify-between">
                <div>
                  {/* Section heading for the listing, not the page heading — the
                      page H1 comes from the Strapi landing-page content above.
                      This was a second <h1> on /shop/<segment>. */}
                  <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">{name}&apos;s Offers</h2>
                </div>
                <div className="tw-flex tw-items-center tw-gap-4">
                  <CustomSortBy items={allSortsCustomTranslated} />
                </div>
              </div>
            </header>

            {/* Mobile Filter Toggle */}
            <div className="tw-mb-6 tw-block xl:tw-hidden">
              <SkusMobileSearchFilters
                viewMode="grid"
                setViewMode={() => {}}
                allSortsCustomTranslated={allSortsCustomTranslated}
                setFiltersAreOpen={setFiltersAreOpen}
                filterConfig={mobileFilterConfig}
              />
            </div>

            {/* Bids Display */}
            <SellerBidsDisplay
              asks={asks}
              onCardClick={handleSellerCardClick}
              maxBottles={maxBottles}
            />

            {/* Pagination */}
            <div className="tw-mt-8 tw-flex tw-justify-center">
              <CustomPagination />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
